import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { BrandApplication } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useAddApplicationNote } from '../hooks/use-add-application-note';
import { noteSchema, type NoteValues } from '../schemas/note-schema';

export interface ApplicationNotesCardProps {
  application: BrandApplication;
  /** Owners and admins write notes; other roles read them. */
  canWrite: boolean;
  className?: string;
}

/** The platform team's notes on an application, oldest first, with a box to add one. */
export function ApplicationNotesCard({
  application,
  canWrite,
  className,
}: ApplicationNotesCardProps) {
  const { t } = useTranslation('applications');
  const format = useFormat();
  const addNote = useAddApplicationNote(application.id);
  const form = useForm<NoteValues>({
    resolver: useI18nResolver(noteSchema),
    defaultValues: { text: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await addNote.mutateAsync(values);
      form.reset({ text: '' });
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Card className={className} data-tour="platform.applications.notes">
      <CardHeader>
        <CardTitle>{t('notes.title')}</CardTitle>
        <CardDescription>{t('notes.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {application.notes.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('notes.empty')}</p>
        ) : (
          <ol className="flex flex-col gap-4 border-l pl-4" aria-label={t('notes.title')}>
            {application.notes.map((note, index) => (
              <li key={`${note.at}-${index}`} className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground text-xs">
                  <span className="text-foreground font-medium">
                    {note.byName ?? t('notes.deletedUser')}
                  </span>{' '}
                  · <time title={format.utc(note.at)}>{format.dateTime(note.at)}</time>
                </span>
                <p className="whitespace-pre-wrap">{note.text}</p>
              </li>
            ))}
          </ol>
        )}
        {canWrite ? (
          <Form {...form}>
            <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('notes.add')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t('notes.placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="self-end" disabled={addNote.isPending}>
                {addNote.isPending ? t('notes.adding') : t('notes.submit')}
              </Button>
            </form>
          </Form>
        ) : null}
      </CardContent>
    </Card>
  );
}
